import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

import { apiClient } from '@/api/apiClient';

export default function Contact() {
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [ticketId, setTicketId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await apiClient.support.contact(formData);
      if (response && response.success) {
        setIsSuccess(true);
        setTicketId(response.ticketNumber);
        toast.success('Votre message a été envoyé avec succès !');
        setFormData({ email: '', subject: '', message: '' });
      } else {
        toast.error("Une erreur s'est produite lors de l'envoi de votre message.");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Une erreur s'est produite lors de l'envoi de votre message.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 md:py-16">
      {/* Header */}
      <div className="text-center mb-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center justify-center p-3 bg-amber-100 dark:bg-amber-950/30 rounded-2xl mb-6 text-amber-600 dark:text-amber-400"
        >
          <Mail className="w-8 h-8" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-amber-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent mb-4"
        >
          Nous Contacter
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-lg text-gray-600 dark:text-gray-400"
        >
          Une question, une suggestion ou un problème ? Laissez-nous un message et nous vous répondrons rapidement.
        </motion.p>
      </div>

      {/* Form Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white/50 dark:bg-[hsl(225,14%,10%)]/50 backdrop-blur-sm rounded-3xl p-6 md:p-10 shadow-sm border border-amber-100 dark:border-[hsl(225,10%,16%)] relative overflow-hidden"
      >
        {isSuccess ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center text-center py-10"
          >
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 text-green-500">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Message envoyé !</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Merci de nous avoir contactés. Nous avons bien reçu votre message et reviendrons vers vous au plus vite.
            </p>
            {ticketId && (
              <div className="bg-gray-50 dark:bg-[hsl(225,14%,10%)] border border-gray-200 dark:border-[hsl(225,10%,20%)] rounded-xl p-4 mb-8 w-full max-w-sm mx-auto">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Votre numéro de ticket</p>
                <p className="font-mono font-bold text-amber-600 dark:text-amber-400 text-lg">{ticketId}</p>
              </div>
            )}
            <Button  
              onClick={() => setIsSuccess(false)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8"
            >
              Envoyer un autre message
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 dark:text-gray-300 font-medium">
                Adresse Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                placeholder="votre@email.com"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-white dark:bg-[hsl(225,14%,10%)] border-gray-200 dark:border-[hsl(225,10%,20%)] focus:ring-amber-600 focus:border-amber-600 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="text-gray-700 dark:text-gray-300 font-medium">
                Objet de la demande
              </Label>
              <select
                id="subject"
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                className="w-full h-10 rounded-xl border border-gray-200 dark:border-[hsl(225,10%,20%)] bg-white dark:bg-[hsl(225,14%,10%)] px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="" disabled>Sélectionnez un objet...</option>
                <option value="question">Question générale</option>
                <option value="bug">Signaler un problème technique</option>
                <option value="suggestion">Suggestion de marque ou fonctionnalité</option>
                <option value="partnership">Demande de partenariat</option>
                <option value="other">Autre</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-700 dark:text-gray-300 font-medium">
                Votre Message
              </Label>
              <textarea
                id="message"
                name="message"
                required
                rows="5"
                placeholder="Comment pouvons-nous vous aider ?"
                value={formData.message}
                onChange={handleChange}
                className="w-full rounded-xl border border-gray-200 dark:border-[hsl(225,10%,20%)] bg-white dark:bg-[hsl(225,14%,10%)] px-3 py-2 text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              ></textarea>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-medium py-6 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer le message
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
              En soumettant ce formulaire, vous acceptez que Pépite utilise vos données pour répondre à votre demande.
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
